import TypographyTranslator from "components/UI/TypographyTranslator"
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

export const TranslatorContext = createContext()

export const useTranslatorContext = () => {
  const context = useContext(TranslatorContext)
  if (context === undefined) {
    throw new Error(
      "useTranslatorContext must be used within a TranslatorProvider"
    )
  }
  return context
}

const TranslatorProvider = ({ children, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedText, setSelectedText] = useState("")

  const mouseDownPosition = useRef({ x: 0, y: 0 })
  const isDragging = useRef(false)

  const selectionRef = useRef(null)
  const selectedTextRef = useRef(null)
  const translatorOpen = useRef(false)

  const handleMouseDown = (e) => {
    mouseDownPosition.current = { x: e.clientX, y: e.clientY }
    isDragging.current = false
  }

  const handleMouseMove = (e) => {
    if (
      e.clientX !== mouseDownPosition.x ||
      e.clientY !== mouseDownPosition.y
    ) {
      isDragging.current = true
    }
  }

  const handleMouseUp = (e) => {
    if (!isDragging?.current) {
      return
    }
    if (
      e?.target?.classList?.contains("translator-typo-content") ||
      translatorOpen.current
    ) {
      return
    }
    const getBoundingClientRect = () => {
      if (
        selectionRef?.current?.anchorNode === null ||
        selectionRef?.current?.type === "None"
      ) {
        return {
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: 0,
          height: 0,
          x: 0,
          y: 0,
        }
      } else {
        return selectionRef?.current?.getRangeAt(0)?.getBoundingClientRect()
      }
    }
    if (selectedTextRef.current?.trim()?.length > 0) {
      const boundingClientRect = getBoundingClientRect()
      if (
        boundingClientRect &&
        boundingClientRect?.width > 0 &&
        boundingClientRect?.height > 0
      ) {
        // get selected text
        setSelectedText(selectedTextRef.current)
        setAnchorEl({ getBoundingClientRect, nodeType: 1 })
      } else {
        setAnchorEl(null)
        setSelectedText("")
      }
    } else {
      setAnchorEl(null)
      setSelectedText("")
    }
  }

  const handleSelectionChange = () => {
    if (translatorOpen.current) {
      return
    }
    const selection = window.getSelection()
    const text = selection.toString()

    selectedTextRef.current = text
    selectionRef.current = selection
    if (text.trim().length === 0) {
      setSelectedText("")
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleMouseDown)
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    document.addEventListener("selectionchange", handleSelectionChange)
    return () => {
      document.removeEventListener("mousedown", handleMouseDown)
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.removeEventListener("selectionchange", handleSelectionChange)
    }
  }, [anchorEl])

  useEffect(() => {
    window.hideTranslatorButton = () => {
      setSelectedText("")
      setAnchorEl(null)
      return true
    }
    return () => {
      window.hideTranslatorButton = undefined
    }
  }, [])

  return (
    <TranslatorContext.Provider value={{}}>
      <TypographyTranslator
        selectionText={selectedText}
        anchorEl={selectedText?.trim()?.length > 0 ? anchorEl : null}
        open={Boolean(anchorEl) && selectedText?.trim()?.length > 0}
        onTranslatorOpen={() => {
          translatorOpen.current = true
        }}
        disabled={disabled}
        setOpen={(value) => {
          if (!value) {
            setAnchorEl(null)
            setSelectedText("")
            translatorOpen.current = false
          }
        }}
      />
      {children}
    </TranslatorContext.Provider>
  )
}

export default TranslatorProvider
